/**
 * service-item.ts
 * locx-service-progress Slice 3/4 — Shared Zod contracts for servicios.service_item
 * (certifiable avance line, instantiated from a service_type's default_items
 * template) and its progress entries (Slice 4).
 *
 * Spec: SL-47 (service_item) / SL-48 (progress entries) /
 * openspec/changes/2026-07-02-locx-service-progress/specs/service-item-progress/spec.md
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// ServiceItemStatusSchema — closed vocabulary (SL-47.1, design §3)
// ---------------------------------------------------------------------------

/**
 * Closed vocabulary: 'pending' | 'in_progress' | 'done' | 'blocked_gate'.
 * This is a PURE DERIVED VALUE computed by computeServiceItemStatus() (design §3)
 * — NOT a user-driven transition, NOT a kernel StateMachine. See SL-48.3.
 *
 * Spec: SL-47.1
 */
export const ServiceItemStatusSchema = z.enum(['pending', 'in_progress', 'done', 'blocked_gate'], {
  errorMap: () => ({ message: "status must be 'pending', 'in_progress', 'done', or 'blocked_gate'" }),
});

export type ServiceItemStatusContract = z.infer<typeof ServiceItemStatusSchema>;

// ---------------------------------------------------------------------------
// EntryModeSchema — closed vocabulary (add-locx-daily-report Slice 7 — SL-56.1)
// ---------------------------------------------------------------------------

/**
 * Closed vocabulary: 'delta' | 'absolute'. Drives which computeQtyDone branch
 * recomputes qtyDone on each progress entry — 'delta' SUMs (default, existing
 * behavior), 'absolute' replaces with the latest-occurredAt entry (avance %
 * items). Informational on this response — set once at instantiation time,
 * never edited by a client request.
 *
 * Spec: SL-56.1, SL-56.3
 */
export const EntryModeSchema = z.enum(['delta', 'absolute'], {
  errorMap: () => ({ message: "entryMode must be 'delta' or 'absolute'" }),
});

export type EntryModeContract = z.infer<typeof EntryModeSchema>;

// ---------------------------------------------------------------------------
// ItemCategorySchema — closed vocabulary (2026-07-21)
// ---------------------------------------------------------------------------

/**
 * Closed vocabulary: 'transport' | 'equipment' | 'labor' | 'other'. Which
 * daily-report line section(s) (Transporte/Equipos/Personal) a service_item
 * is offered in. Optional and backward-compatible: existing default_items
 * templates/instantiated items predate this field entirely. Missing/undefined
 * means "unclassified — offered in every section", the exact behavior every
 * item had before this field existed, so no already-configured company's
 * daily-report flow changes just because this shipped. Only newly-classified
 * items narrow which section(s) they appear in — see DailyReportForm.vue's
 * per-step visibility (2026-07-21).
 */
export const ItemCategorySchema = z.enum(['transport', 'equipment', 'labor', 'other'], {
  errorMap: () => ({ message: "category must be 'transport', 'equipment', 'labor', or 'other'" }),
});

export type ItemCategoryContract = z.infer<typeof ItemCategorySchema>;

// ---------------------------------------------------------------------------
// ServiceItemUpdateSchema — DRAFT-only qtyTotal/unitPrice edit (SL-47.4)
// ---------------------------------------------------------------------------

/**
 * Request body for PATCH /servicios/orders/:id/items/:itemId.
 * At least one of qtyTotal/unitPrice must be provided.
 * Numeric fields are strings (e.g. '1500.00') — mirrors AssetPricingSchema /
 * RateCardItemCreateSchema convention (Postgres numeric columns).
 *
 * Spec: SL-47.4, SL-47.5
 */
export const ServiceItemUpdateSchema = z
  .object({
    qtyTotal: z
      .string()
      .regex(/^\d+(\.\d+)?$/, 'qtyTotal must be a numeric string (e.g. "1000.00")')
      .nullable()
      .optional(),
    unitPrice: z
      .string()
      .regex(/^\d+(\.\d+)?$/, 'unitPrice must be a numeric string (e.g. "1500.00")')
      .nullable()
      .optional(),
  })
  .strict()
  .refine((data) => data.qtyTotal !== undefined || data.unitPrice !== undefined, {
    message: 'At least one of qtyTotal or unitPrice must be provided.',
  });

export type ServiceItemUpdate = z.infer<typeof ServiceItemUpdateSchema>;

// ---------------------------------------------------------------------------
// ServiceItemResponseSchema (SL-47.5)
// ---------------------------------------------------------------------------

export const ServiceItemResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceOrderId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  unit: z.string(),
  qtyTotal: z.string().nullable(),
  qtyDone: z.string(),
  unitPrice: z.string().nullable(),
  rateCardItemId: z.string().uuid().nullable(),
  requiresQualityGate: z.boolean(),
  status: ServiceItemStatusSchema,
  entryMode: EntryModeSchema,
  category: ItemCategorySchema.nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
});

export type ServiceItemResponse = z.infer<typeof ServiceItemResponseSchema>;

// ---------------------------------------------------------------------------
// ProgressEntryCreateSchema (SL-48.1, SL-48.5) — Slice 4
// ---------------------------------------------------------------------------

/**
 * Request body for POST /servicios/orders/:id/items/:itemId/progress.
 * qty MUST be a positive numeric string (SL-48.5) — zero/negative rejected
 * before any DB write (corrections via negative qty are P1, not this change).
 *
 * Spec: SL-48.1, SL-48.5, SL-48.6
 */
export const ProgressEntryCreateSchema = z
  .object({
    qty: z
      .string()
      .regex(/^\d+(\.\d+)?$/, 'qty must be a positive numeric string (e.g. "320.00")')
      .refine((v) => Number(v) > 0, 'qty must be greater than zero'),
    occurredAt: z.string().datetime({ offset: true }),
    notes: z
      .string()
      .max(2000, 'notes must be at most 2000 characters')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
  })
  .strict();

export type ProgressEntryCreate = z.infer<typeof ProgressEntryCreateSchema>;

// ---------------------------------------------------------------------------
// ProgressEntryResponseSchema
// ---------------------------------------------------------------------------

export const ProgressEntryResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceItemId: z.string().uuid(),
  qty: z.string(),
  occurredAt: z.string().datetime().or(z.date()),
  notes: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string().datetime().or(z.date()),
});

export type ProgressEntryResponse = z.infer<typeof ProgressEntryResponseSchema>;

// ---------------------------------------------------------------------------
// SyncOrderItemsResponseSchema (SL-58) — add-locx-daily-report Slice 8
// ---------------------------------------------------------------------------
// Response for POST /servicios/orders/:id/items/sync — diff-and-instantiate
// catalog drift onto an already-created order.
// ---------------------------------------------------------------------------

export const SyncOrderItemsResponseSchema = z.object({
  insertedItems: z.array(ServiceItemResponseSchema),
  skippedCount: z.number().int(),
});

export type SyncOrderItemsResponse = z.infer<typeof SyncOrderItemsResponseSchema>;
